<template>
  <div>
    <!-- intro -->
    <div
      class="intro-banner"
      data-background-image="images/home-background.jpg"
    >
      <div class="container">
        <!-- Intro Headline -->
        <div class="row">
          <div class="col-md-12">
            <div class="banner-headline">
              <h3>
                <strong>Hire experts or be hired for any job, any time.</strong>
                <br />
                <span
                  >Thousands of small businesses use
                  <strong class="color">{{ appName }}</strong> to turn their
                  ideas into reality.</span
                >
              </h3>
            </div>
          </div>
        </div>

        <!-- Search Bar -->
        <div class="row">
          <div class="col-md-12">
            <div class="intro-banner-search-form margin-top-95">
              <!-- Search Field -->
              <div class="intro-search-field">
                <label for="intro-keywords" class="field-title ripple-effect"
                  >What job you want?</label
                >
                <input
                  id="intro-keywords"
                  type="text"
                  v-model="searchTerm"
                  placeholder="search projects using title, description or address"
                />
              </div>

              <!-- Button -->
              <div class="intro-search-button">
                <router-link
                  :to="`/projects?searchTerm=${searchTerm}`"
                  class="button ripple-effect"
                  >Search</router-link
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Stats -->
        <div class="row">
          <div class="col-md-12">
            <ul class="intro-stats margin-top-45 hide-under-992px">
              <li>
                <strong class="counter">{{ totalJobs }}</strong>
                <span>Jobs Posted</span>
              </li>
              <li>
                <strong class="counter">{{ totalUsers }}</strong>
                <span>Freelancers</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Category Boxes -->
    <div class="section margin-top-65">
      <div class="container">
        <div class="row">
          <div class="col-xl-12">
            <div class="section-headline centered margin-bottom-15">
              <h3>Popular Job Categories</h3>
            </div>

            <!-- Category Boxes Container -->
            <div class="categories-container">
              <router-link
                v-for="(category, index) in popularCategories"
                :key="index"
                :to="`projects?taskId=${category.id}`"
                class="category-box"
              >
                <div class="category-box-counter">
                  {{ category.projects_count }} project(s)
                </div>
                <div class="category-box-content">
                  <h3>{{ category.name }}</h3>
                  <p>{{ stringifyTaskList(category.sub_tasks) }} ...</p>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Jobs -->
    <div class="section gray margin-top-45 padding-top-65 padding-bottom-75">
      <div class="container">
        <div class="row">
          <div class="col-xl-12">
            <!-- Section Headline -->
            <div class="section-headline margin-top-0 margin-bottom-35">
              <h3>Featured Jobs</h3>
              <router-link class="headline-link" to="/projects"
                >Browse All Jobs</router-link
              >
            </div>

            <!-- Jobs Container -->
            <div class="listings-container compact-list-layout margin-top-35">
              <router-link
                v-for="(project, index) in featuredJobs"
                :key="index"
                :to="`/project-details/${project.id}`"
                class="job-listing with-apply-button"
              >
                <!-- Job Listing Details -->
                <div class="job-listing-details">
                  <div class="job-listing-description">
                    <h3 class="job-listing-title">
                      {{ project.title }}
                    </h3>

                    <!-- Job Listing Footer -->
                    <div class="job-listing-footer">
                      <ul>
                        <li>
                          <i class="icon-material-outline-money"></i>
                          {{ formatAmount(project.amount_paid) }}
                          <div
                            class="verified-badge"
                            title="Payment verified"
                            data-tippy-placement="top"
                          ></div>
                        </li>
                        <li>
                          <i class="icon-material-outline-business"></i>
                          {{
                            project.country ? project.country.name : "Remote"
                          }}
                        </li>
                        <li>
                          <i class="icon-material-outline-location-on"></i>
                          {{ project.task.name }}
                        </li>
                        <li>
                          <i class="icon-material-outline-business-center"></i>
                          {{ project.subtask.name }}
                        </li>
                        <li>
                          <i class="icon-material-outline-access-time"></i>
                          {{ formatDate(project.updated_at) }}
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Apply Button -->
                  <span class="list-apply-button ripple-effect">Apply Now</span>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Featured Jobs / End -->

    <!-- Features Cities -->
    <div class="section margin-top-65 margin-bottom-65">
      <div class="container">
        <div class="row">
          <!-- Section Headline -->
          <div class="col-xl-12">
            <div
              class="section-headline centered margin-top-0 margin-bottom-45"
            >
              <h3>Featured Regions</h3>
            </div>
          </div>

          <div
            v-for="(region, index) in featuredCities"
            :key="index"
            class="col-xl-3 col-md-6"
          >
            <!-- Photo Box -->
            <router-link
              :to="`/project-details?region_id=${region.id}`"
              class="photo-box photo-box-image "
              data-background-image="@/assets/images/projects_count.png"
            >
              <div class="photo-box-content">
                <h3>{{ region.name }}</h3>
                <span>{{ region.projects_count }} Jobs</span>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
    <!-- Features Cities / End -->

    <!-- Highest Rated Freelancers -->
    <div
      class="
        section
        gray
        padding-top-65 padding-bottom-70
        full-width-carousel-fix
      "
    >
      <div class="container">
        <div class="row">
          <div class="col-xl-12">
            <!-- Section Headline -->
            <div class="section-headline margin-top-0 margin-bottom-25">
              <h3>Highest Rated Freelancers</h3>
              <a href="freelancers-grid-layout.html" class="headline-link"
                >Browse All Freelancers</a
              >
            </div>
          </div>

          <!--Freelancer -->
          <div class="col-xl-12">
            <div class="freelancers-container freelancers-grid-layout">
              <div
                v-for="(freelancer, index) in topFreelancers"
                :key="index"
                class="freelancer"
              >
                <!-- Overview -->
                <div class="freelancer-overview">
                  <div class="freelancer-overview-inner">
                
                    <!-- Avatar -->
                    <div class="freelancer-avatar">
                      <div class="verified-badge"></div>
                      <!-- <a href="freelancersingle-freelancer-profile.html" -->
                      <img
                        :src="
                          freelancer.avatar
                            ? freelancer.avatar
                            : 'https://media.istockphoto.com/vectors/user-icon-flat-isolated-on-white-background-user-symbol-vector-vector-id1300845620?b=1&k=20&m=1300845620&s=170667a&w=0&h=JbOeyFgAc6-3jmptv6mzXpGcAd_8xqkQa_oUK2viFr8='
                        "
                        :alt="freelancer.name"
                      />
                      <!-- </a> -->
                    </div>

                    <!-- Name -->
                    <div class="freelancer-name">
                      <h4>
                        <a href="single-freelancer-profile.html"
                          >{{ freelancer.name }}
                        </a>
                      </h4>
                      <span>{{ freelancer.title }}</span>
                    </div>
                  </div>
                </div>

                <!-- Details -->
                <div class="freelancer-details">
                  <div class="freelancer-details-list">
                    <ul>
                      <li>
                        Location
                        <strong
                          ><i class="icon-material-outline-location-on"></i>
                          {{
                            freelancer.region ? freelancer.region.name : "N/A"
                          }}</strong
                        >
                      </li>
                      <li>Ratings <strong>{{freelancer.ratings}}/5</strong></li>
                      <li>
                        Projects Applications
                        <strong>{{ freelancer.my_applications_count }}</strong>
                      </li>
                    </ul>
                  </div>
                  <a
                    href="single-freelancer-profile.html"
                    class="button button-sliding-icon ripple-effect"
                    >View Profile
                    <i class="icon-material-outline-arrow-right-alt"></i
                  ></a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Highest Rated Freelancers / End-->
  </div>
</template>

<script>
/* eslint-disable no-console */
//   import AppSelect2 from '../../ui/AppSelect2.vue';
import * as $ from "jquery";
import "slick-carousel";
import { getCall } from "../../../core/services/http.service";
import {
  ALL_TASKS,
  ALL_TASKS_AND_SUBTASKS,
} from "../../../core/config/endpoints/tasks";
import { DOMAIN_NAME } from "../../../plugins/config/constants";
import {
  formatAmount,
  formatDate,
  stringifyArrayWithKey,
  subString,
} from "../../../plugins/handlers";

export default {
  name: "Home",
  components: {
    //   AppSelect2,
  },
  data() {
    return {
      searchTerm: "",
      sometext: "",
    };
  },

  mounted: function() {
    if (!this.services) return this.getAllTasks();
    /* index */
    $(".freelance-slider").slick({
      slidesToShow: 4,
      slidesToScroll: 1,
      arrows: true,
      fade: false,
      responsive: [
        {
          breakpoint: 1099,
          settings: {
            slidesToShow: 4,
          },
        },
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 2,
          },
        },
        {
          breakpoint: 600,
          settings: {
            slidesToShow: 1,
          },
        },
      ],
    });
    $(".service-slider").slick({
      slidesToShow: 5,
      slidesToScroll: 1,
      arrows: true,
      fade: false,
      responsive: [
        {
          breakpoint: 1099,
          settings: {
            slidesToShow: 4,
          },
        },
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 3,
          },
        },
        {
          breakpoint: 600,
          settings: {
            slidesToShow: 2,
          },
        },
      ],
    });
  },

  computed: {
    landingPageDetails() {
      return this.$store.state.landingPageDetails;
    },

    popularCategories() {
      return this.landingPageDetails.popularCategories;
    },

    featuredJobs() {
      return this.landingPageDetails.featuredJobs;
    },

    totalJobs() {
      return this.landingPageDetails.totalJobs;
    },

    totalUsers() {
      return this.landingPageDetails.totalUsers;
    },

    featuredCities() {
      return this.landingPageDetails.featuredCities;
    },

    topFreelancers() {
      return this.landingPageDetails.topFreelancers;
    },

    services() {
      return this.$store.state.allTasksAndSubTasks
        ? this.$store.state.allTasksAndSubTasks
        : false;
    },

    appName() {
      return DOMAIN_NAME;
    },
  },

  methods: {
    getAllTasks() {
      getCall(ALL_TASKS_AND_SUBTASKS)
        .then(response => {
          let payload = {
            key: "allTasksAndSubTasks",
            value: response.data.data,
          };
          this.$store.commit("commitItem", payload);
        })
        .catch(error => {
          console.log(error);
        });
    },

    stringifyTaskList(myArray) {
      let finalString = stringifyArrayWithKey(myArray);
      return subString(finalString, 150);
    },

    searchServices: function() {
      getCall(ALL_TASKS)
        .then(response => {
          this.sometext = response.data;
          console.log(response.data);
        })
        .catch(error => {
          console.log(error);
        });
    },

    formatDate: function(date) {
      return formatDate(date);
    },

    formatAmount: function(amount) {
      return formatAmount(amount);
    },
  },
};
</script>

<style scoped>
.btn-action {
  width: 13.75rem;
  padding-top: 21px;
  padding-bottom: 21px;
}

.testi-wrap h2 {
  font-size: 26px;
  line-height: 38px;
  padding-bottom: 24px;
  color: #222325;
  font-weight: bold;
  margin-bottom: 0;
}

.testi-wrap {
  padding-bottom: 4em;
}

.photo-box-image {
  background: url("/assets/images/company-logo-05.png");
}
</style>
